/** The small amount of formatting a study note actually needs.
 *
 * The client asked to "highlight / bold the text". A full rich-text editor
 * would mean storing HTML, and storing HTML written by a user means
 * sanitising it forever. So notes stay PLAIN TEXT with two markers:
 *
 *     **bold**        and        ==highlight==
 *
 * which is what a learner would type anyway. Rendering builds React
 * elements from the parsed pieces — no dangerouslySetInnerHTML anywhere, so
 * there is nothing to sanitise and nothing that can escape.
 */
import type { ReactNode } from "react";

type Piece = { kind: "text" | "bold" | "mark"; text: string };

/** Split a note into plain, bold and highlighted pieces, in order. */
export function parseNote(source: string): Piece[] {
  const pieces: Piece[] = [];
  // Non-greedy so `**a** and **b**` is two bold runs, not one long one.
  const pattern = /(\*\*(.+?)\*\*|==(.+?)==)/gs;
  let at = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(source)) !== null) {
    if (m.index > at) pieces.push({ kind: "text", text: source.slice(at, m.index) });
    if (m[2] !== undefined) pieces.push({ kind: "bold", text: m[2] });
    else pieces.push({ kind: "mark", text: m[3] });
    at = m.index + m[0].length;
  }
  if (at < source.length) pieces.push({ kind: "text", text: source.slice(at) });
  return pieces;
}

export function RichNote({ content }: { content: string }) {
  return (
    <p className="whitespace-pre-wrap text-sm text-ink">
      {parseNote(content).map((piece, i) => renderPiece(piece, i))}
    </p>
  );
}

function renderPiece(piece: Piece, key: number): ReactNode {
  if (piece.kind === "bold") return <strong key={key}>{piece.text}</strong>;
  if (piece.kind === "mark") {
    return (
      <mark key={key} className="rounded-sm bg-warning/25 px-0.5 text-ink">
        {piece.text}
      </mark>
    );
  }
  return <span key={key}>{piece.text}</span>;
}

/** Wrap the current selection in `marker`, or insert an empty pair with the
 *  caret between them when nothing is selected. Returns the new value and
 *  where the caret should land. */
export function applyMarker(
  value: string,
  start: number,
  end: number,
  marker: string,
): { value: string; caret: number } {
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${marker}${selected}${marker}${value.slice(end)}`;
  // Nothing selected: sit between the markers, ready to type. Something
  // selected: land after it, ready to carry on.
  const caret = selected
    ? end + marker.length * 2
    : start + marker.length;
  return { value: next, caret };
}
